/**
 * Agents — one card per agent, ONE decision each: which ENGINE it runs.
 *
 * The ontology this page teaches (CTO 09-22, ground-up revamp):
 *   agents  = Planner, Build, QA — the three AI workers customers meet.
 *   engine  = Claude Code or Codex — the harness an agent drives.
 *   swarm   = the model bucket every agent draws from (Swarm tab) — which
 *             MODEL actually serves a run is the swarm's call, not set here.
 *
 * Each card: what the agent does, its default engine (LaneConfig.engine — read
 * at runtime by resolveBuilderEngineForLane; members with their own runtime
 * choice override it for THEIR runs only), and a quiet "runs on" summary.
 * "Limit swarm models" is progressive disclosure over LaneConfig
 * allowedModels/defaultModel — never-brick: everything allowed until curated.
 *
 * Expert workspace + delivered apps are listed below the fold: real surfaces,
 * but not agents an admin routes here (expert picks their own engine in the
 * workbench; delivered apps get a default swarm model, no engine concept).
 */

import * as React from "react";
import { Brain, ChevronDown, ChevronRight, Cpu, Eye, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { adminFetch } from "./adminFetch";

type LaneKind = "planner" | "execution" | "qa" | "developer" | "runtime";
type LaneEngine = "claude-code" | "codex" | "aa-native";

interface LaneCfg {
  lane: string;
  defaultModel: string | null;
  allowedModels: string[];
  engine: LaneEngine | null;
}
interface ModelRow {
  value: string;
  label: string;
  tier: string;
  provider: string;
  enabled: boolean;
  engines?: string[];
  isDefault?: boolean;
}
interface ProviderRow {
  key: string;
  label: string;
  kind: "open-host" | "vendor";
  isActive: boolean;
}

const AGENTS: { kind: LaneKind; name: string; what: string; icon: React.ComponentType<{ className?: string }>; engineLocked?: string }[] = [
  { kind: "planner", name: "Planner", what: "Talks with the customer — scoping, questions, plans.", icon: Brain },
  { kind: "execution", name: "Build", what: "Executes approved work — builds tickets unattended.", icon: Cpu },
  {
    kind: "qa",
    name: "QA",
    what: "Independently verifies every build before it counts as done.",
    icon: ShieldCheck,
  },
];

export function AgentsPanel() {
  const [lanes, setLanes] = React.useState<Record<string, LaneCfg>>({});
  const [bucket, setBucket] = React.useState<Record<string, number>>({});
  const [models, setModels] = React.useState<ModelRow[]>([]);
  const [activeHost, setActiveHost] = React.useState<ProviderRow | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState<Record<string, boolean>>({});

  const load = React.useCallback(async () => {
    try {
      const [lRes, mRes, pRes] = await Promise.all([
        adminFetch("/api/admin/lanes"),
        adminFetch("/api/admin/models"),
        adminFetch("/api/admin/providers"),
      ]);
      const [l, m, p] = await Promise.all([lRes.json(), mRes.json(), pRes.json()]);
      const cfgs: Record<string, LaneCfg> = {};
      if (Array.isArray(l?.lanes)) for (const c of l.lanes) cfgs[c.lane] = c;
      setLanes(cfgs);
      // Server-computed by the swarm router itself — the SAME candidate count
      // a run's pick draws from, so this readout can never drift from truth.
      setBucket(l?.swarmBucket && typeof l.swarmBucket === "object" ? l.swarmBucket : {});
      setModels(Array.isArray(m?.models) ? m.models : []);
      const act = (Array.isArray(p?.providers) ? p.providers : []).find((x: ProviderRow) => x.kind !== "vendor" && x.isActive) || null;
      setActiveHost(act);
    } catch {
      toast.error("Failed to load agents");
    } finally {
      setLoading(false);
    }
  }, []);
  React.useEffect(() => { void load(); }, [load]);

  const cfg = (kind: LaneKind): LaneCfg => lanes[kind] ?? { lane: kind, defaultModel: null, allowedModels: [], engine: null };

  const patchLane = async (kind: LaneKind, patch: Record<string, unknown>) => {
    const prev = lanes;
    setLanes((s) => ({ ...s, [kind]: { ...cfg(kind), ...patch } as LaneCfg }));
    try {
      const res = await adminFetch("/api/admin/lanes", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lane: kind, patch }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || "save failed");
      // Engine/allow-list edits change the router's candidate set — refresh so
      // the "N in the bucket" figure stays the router's own number.
      void load();
    } catch (e: any) {
      setLanes(prev);
      toast.error(e?.message || "Couldn't save");
    }
  };

  // In-swarm rows the active host serves (open) — the curation universe for a lane.
  const hostModels = activeHost ? models.filter((m) => m.provider === activeHost.key && m.enabled) : [];

  const isAllowed = (c: LaneCfg, m: ModelRow) => (c.allowedModels.length === 0 ? true : c.allowedModels.includes(m.value));


  const toggleAllowed = (kind: LaneKind, m: ModelRow, next: boolean) => {
    const c = cfg(kind);
    const enabledValues = hostModels.map((x) => x.value);
    const base = c.allowedModels.length ? new Set(c.allowedModels) : new Set(enabledValues);
    if (next) base.add(m.value); else base.delete(m.value);
    const arr = Array.from(base);
    const coversAll = enabledValues.length > 0 && enabledValues.every((v) => base.has(v)) && arr.every((v) => enabledValues.includes(v));
    void patchLane(kind, { allowedModels: coversAll ? [] : arr });
  };

  if (loading) return <p className="text-xs text-[var(--dash-text-muted)]">Loading…</p>;

  const EngineControl = ({ kind }: { kind: LaneKind }) => {
    const c = cfg(kind);
    const meta = AGENTS.find((a) => a.kind === kind)!;
    if (meta.engineLocked) {
      return (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-md bg-[var(--dash-bg-muted)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--dash-text)]">Claude Code</span>
          <span className="text-[11.5px] text-[var(--dash-text-muted)]">{meta.engineLocked}</span>
        </div>
      );
    }
    const current = c.engine === "codex" ? "codex" : c.engine === "aa-native" ? "aa-native" : "claude-code";
    return (
      <div className="flex items-center gap-2">
        <div className="inline-flex rounded-[10px] bg-[var(--dash-bg-muted)] p-0.5">
          {(["claude-code", "codex"] as const).map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => void patchLane(kind, { engine: e })}
              className={
                "rounded-md px-3 py-1.5 text-[12.5px] font-semibold transition-colors " +
                (current === e ? "bg-[var(--dash-panel-bg)] text-[var(--dash-text)] shadow-sm" : "text-[var(--dash-text-secondary)] hover:text-[var(--dash-text)]")
              }
            >
              {e === "claude-code" ? "Claude Code" : "Codex"}
            </button>
          ))}
        </div>
        {current === "aa-native" && (
          <span className="text-[11px] text-[#d92d43]">currently a legacy value (AA-native) — pick an engine</span>
        )}
      </div>
    );
  };

  const Curation = ({ kind }: { kind: LaneKind }) => {
    const c = cfg(kind);
    const isOpen = !!open[kind];
    return (
      <div className="mt-3 border-t border-[var(--dash-border)] pt-2.5">
        <button
          type="button"
          onClick={() => setOpen((s) => ({ ...s, [kind]: !isOpen }))}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-[var(--dash-text-secondary)] hover:text-[var(--dash-text)]"
        >
          {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Limit swarm models for this agent
          <span className="text-[11px] font-normal text-[var(--dash-text-muted)]">
            · {c.allowedModels.length === 0 ? "all allowed" : `${c.allowedModels.length} allowed`}
          </span>
        </button>
        {isOpen && (
          <div className="mt-2">
            {!activeHost || hostModels.length === 0 ? (
              <p className="text-[12px] text-[var(--dash-text-muted)]">Nothing to limit yet — collect models into the swarm first (Swarm tab).</p>
            ) : (
              <div className="max-h-[300px] divide-y divide-[var(--dash-border)] overflow-y-auto rounded-lg border border-[var(--dash-border)]">
                {hostModels.map((m) => {
                  const allowed = isAllowed(c, m);
                  const isDefault = c.defaultModel === m.value;
                  return (
                    <div key={m.value} className="flex items-center gap-3 px-3 py-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={allowed}
                        onChange={(e) => toggleAllowed(kind, m, e.target.checked)}
                        aria-label={`Allow ${m.label}`}
                      />
                      <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--dash-text)]">{m.label}</span>
                      <label className="inline-flex items-center gap-1 text-[11px] text-[var(--dash-text-muted)]" title="The router tries this one first — the swarm still decides per run (and moves on when it fails)">
                        <input
                          type="radio"
                          name={`pref-${kind}`}
                          className="h-3.5 w-3.5"
                          checked={isDefault}
                          disabled={!allowed}
                          onChange={() => void patchLane(kind, { defaultModel: m.value })}
                        />
                        first pick
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="mt-1.5 text-[11px] text-[var(--dash-text-muted)]">
              Unchecked models never serve this agent. Leave everything checked to let the whole swarm serve it.
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {AGENTS.map(({ kind, name, what, icon: Icon }) => (
        <div key={kind} className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-panel-bg)] p-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="flex min-w-[220px] items-center gap-2.5">
              <Icon className="h-4 w-4 text-[var(--dash-text-muted)]" />
              <span>
                <span className="block text-[14px] font-semibold text-[var(--dash-text)]">{name}</span>
                <span className="block text-[12px] text-[var(--dash-text-secondary)]">{what}</span>
              </span>
            </span>
            <span className="ml-auto"><EngineControl kind={kind} /></span>
          </div>
          {/* NO single "runs on" model — that would misstate the product: an
              agent is never married to one model (CTO 09-22). The model is the
              swarm's per-run call over the bucket; this line only says how big
              that bucket currently is for this agent's engine. */}
          <p className="mt-2.5 text-[12px] text-[var(--dash-text-muted)]">
            Models: <b className="text-[var(--dash-text-secondary)]">the swarm&apos;s call</b>
            {" — "}
            {(bucket[kind] ?? 0) > 0
              ? `${bucket[kind]} in the bucket for this engine`
              : "nothing servable in the bucket yet — stock the Swarm tab"}
            {" — members who set their own engine override this for their runs only."}
          </p>
          <Curation kind={kind} />
        </div>
      ))}

      {/* Not agents — two other AI surfaces, honestly separated. */}
      <div className="mt-1 rounded-xl border border-dashed border-[var(--dash-border)] p-4">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--dash-text-muted)]">Other AI surfaces</p>
        <div className="flex flex-col gap-2 text-[12.5px] text-[var(--dash-text-secondary)]">
          <p>
            <b className="text-[var(--dash-text)]">Expert workspace</b> — each expert picks their own engine in their workbench;
            per-expert model access is managed on <Link className="underline hover:text-[var(--dash-text)]" to="/super/machine/people">the Experts page</Link>.
          </p>
          <p className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-[var(--dash-text-muted)]" />
            <span>
              <b className="text-[var(--dash-text)]">Delivered apps</b> — AI features inside apps we ship run on the swarm too; their default model follows the swarm&apos;s Build preference.
            </span>
          </p>
        </div>
      </div>

      <p className="flex items-center gap-1.5 px-1 text-[11.5px] text-[var(--dash-text-muted)]">
        <Eye className="h-3 w-3" />
        Which model serves a run is the swarm&apos;s call — stock and price the bucket on the Swarm tab.
      </p>
    </div>
  );
}
